import React, { FC, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { DEF_PG_SIZE_SUB } from "../../constants/maximum-page-size";
import { useLocale } from "../../contexts/localeContext";
import { TableContextProvider } from "../../contexts/table/TableContextProvider";
import { TranslationKeys } from "../../contexts/TranslationKeys";
import UserFilterColumns from "../../models/ColumnDefinitions/UserFilterColumns";
import { DataFilterPreference } from "../../models/DateFilterPreference";
import { ListData } from "../../models/ListData";
import { ListQuery } from "../../models/ListQuery";
import { Lookup } from "../../models/Lookup";
import { DeleteEntityResult } from "../../models/services/DeleteEntityResult";
import { deleteUserPreference, getUserDataFiltersByLoginId, getUserFiltersByIdForUserLogin, savePreference } from "../../services/userPreferenceService";
import { getErrorMessage, showLoadItemError } from "../../services/utilities/ServiceError";
import ActivityFrame from "../activity-frame/activity-frame";
import { useDialog } from "../dialog/DialogContext";
import TableTab from "../tabs/table-tab";
import { DataFilterWizard } from "./dataFilterWizard";

interface Props {
    itemId: string,
    onDeleteFilter: (createNew) => void,
    onSave: (createNew) => void;
}

const DataFilters: FC<Props> = ({ itemId, onDeleteFilter, onSave }) => {
    const { translate } = useLocale();
    const title = translate.byKey(TranslationKeys.assignedFilters);
    const [lookup, setLookup] = useState<Lookup[]>([]);
    const [showModal, setShowModal] = useState(false);
    const [busy, setBusy] = useState(false);
    const initialValues: DataFilterPreference = {
        id: 0,
        type: '',
        columnId: '',
        userLoginId: itemId,
        settingDatas: [],
        preferenceTypeId: '',
        oldSelectedFilters: '',
        settingDataObjects: {}
    };
    const [currentItem, setCurrentItem] = useState<DataFilterPreference>(initialValues);
    const dialog = useDialog();

    useEffect(() => {
        (async () => {
            dataRefresh((query) => getDataFiltersApplied(query));
        })();
        setCurrentItem(initialValues);
    }, [itemId]);

    const getDataFiltersApplied = (listParameters) => {
        return getUserDataFiltersByLoginId(itemId, listParameters);
    }

    // default to a method that returns nothingp
    // when putting a method in a state variable, useState requires us to return the method from a method, hence the wrapped inner method.
    const [dataRefresh, setDataRefresh] = useState(() => (dataMethod?: (query?: ListQuery) => Promise<ListData>) => { return });

    const manualDataRefresh = (refreshData: (dataMethod?: (query?: ListQuery) => Promise<ListData>) => void) => {
        setDataRefresh(() => refreshData);
    }
    // <-- end context

    const handleSave = (userPreference) => {
        setBusy(true);
        const saveNew = currentItem.id ? false : true
        savePreference(userPreference, saveNew)
            .then(success => {
                onSave(saveNew);
                setBusy(false);
                toast.success(translate.byKey(TranslationKeys.permissionSaved));
            })
            .catch(failure => {
                dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(failure) });
                setBusy(false);
            });
    }

    const editDataFilter = async (id: string) => {
        try {
            const userPreference = await getUserFiltersByIdForUserLogin(id);
            setCurrentItem(userPreference);
            setShowModal(true);
        } catch (error) {
            showLoadItemError(error, dialog, translate, () => { setShowModal(false); });
        }
    }

    const onClose = () => {
        setShowModal(false);
        setCurrentItem(initialValues);
    }

    const onDelete = (id: string): Promise<DeleteEntityResult> => {
        setBusy(true);
        return new Promise((resolve, reject) => {
            deleteUserPreference(id)
                .then(
                    deleteResult => {
                        resolve(deleteResult);
                        onDeleteFilter(true);
                        setBusy(false);
                    },
                    deleteResult => {
                        reject(deleteResult);
                        setBusy(false);
                    })
        });
    }

    return <>
        <ActivityFrame busy={busy} className='flex flex-col flex-grow bg-white'>
            <TableContextProvider
                dataMethod={(query) => getDataFiltersApplied(query)}
                manualDataRefresh={manualDataRefresh}
                tableDescriptor={UserFilterColumns}
                deleteMethod={onDelete}
                editMethod={editDataFilter}
                useTable
                usePagination
                maxPageSize={DEF_PG_SIZE_SUB}
                disableSearch={true}
                lookup={lookup}>
                <TableTab
                    title={title}
                    selectRows={false}
                    cyName="dataFilters">
                    <button type="button" className="flex px-4 bg-lime rounded-lg shadow text-white text-sm items-center"
                        id="dataFiltersManageBtn"
                        onClick={() => { setShowModal(true) }}>
                        {translate.byKey(TranslationKeys.manage)}
                    </button>
                    <DataFilterWizard
                        isOpen={showModal}
                        onClose={onClose}
                        title={translate.byKey(currentItem.id ? TranslationKeys.editDataFilter : TranslationKeys.addDataFilter)}
                        currentItem={currentItem}
                        onSave={handleSave}>
                    </DataFilterWizard>
                </TableTab>
            </TableContextProvider>
        </ActivityFrame>
    </>
};

export default DataFilters;
